import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FolderPlus,
  MessageSquarePlus,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeft,
  Trash2,
  Pencil,
  Download,
  Settings,
  SquarePen,
  FolderInput,
  FolderOutput,
  LogOut,
  Search,
  Folder,
} from 'lucide-react';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getAvatarShape } from '@/lib/avatarShape';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import type { SettingsTab } from './SettingsModal';
import type { Chat } from '@/lib/workspace/types';
import { WorkspaceStatusChips } from './WorkspaceStatusChips';

interface WorkspaceSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onOpenSettings: (tab?: SettingsTab) => void;
  onOpenProjectSettings?: () => void;
  /** Open Claude-style project hub (instructions / memory / context / chats). */
  onOpenProjectHub?: (projectId: string) => void;
  /** True when the project hub is the main view (not a chat). */
  projectHubOpen?: boolean;
  /** Leave hub view (e.g. when opening a chat). */
  onLeaveProjectHub?: () => void;
  /** Open ⌘K search */
  onOpenSearch?: () => void;
}

function displayName(account: Account): string {
  return account.metadata.name || account.metadata.display_name || genUserName(account.pubkey);
}

const RECENT_LIMIT = 30;

export function WorkspaceSidebar({
  collapsed,
  onToggle,
  onOpenSettings,
  onOpenProjectSettings,
  onOpenProjectHub,
  projectHubOpen = false,
  onLeaveProjectHub,
  onOpenSearch,
}: WorkspaceSidebarProps) {
  const {
    projects,
    chats,
    recentChats,
    activeProject,
    activeChat,
    selectProject,
    selectChat,
    createProject,
    createChat,
    deleteChat,
    deleteProject,
    updateProject,
    updateChat,
    exportProject,
    moveChatToProject,
  } = useWorkspace();

  const { currentUser, otherUsers, setLogin, removeLogin } = useLoggedInAccounts();

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const isExpanded = (projectId: string) =>
    expanded[projectId] ?? activeProject?.id === projectId;

  const visibleRecents = useMemo(
    () => recentChats.slice(0, RECENT_LIMIT),
    [recentChats],
  );

  useEffect(() => {
    if (renamingId) {
      const t = window.setTimeout(() => {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      }, 10);
      return () => clearTimeout(t);
    }
  }, [renamingId]);

  const startRename = (id: string, current: string, kind: 'project' | 'chat') => {
    setRenamingId(`${kind}:${id}`);
    setRenameValue(current);
  };

  const commitRename = async () => {
    if (!renamingId) return;
    const [kind, id] = renamingId.split(':');
    const name = renameValue.trim();
    if (name) {
      if (kind === 'project') await updateProject(id, { name });
      else await updateChat(id, { title: name });
    }
    setRenamingId(null);
  };

  const openProjectEdit = (projectId: string) => {
    if (onOpenProjectHub) {
      onOpenProjectHub(projectId);
      return;
    }
    void selectProject(projectId).then(() => {
      if (onOpenProjectSettings) onOpenProjectSettings();
      else onOpenSettings('instructions');
    });
  };

  const openProject = (projectId: string) => {
    setExpanded((e) => ({ ...e, [projectId]: true }));
    if (onOpenProjectHub) {
      onOpenProjectHub(projectId);
    } else {
      void selectProject(projectId);
    }
  };

  const renderChatRow = (chat: Chat, opts?: { indent?: boolean }) => {
    const isActive = activeChat?.id === chat.id;
    return (
      <div
        key={chat.id}
        className={cn(
          'group flex items-center gap-0.5 rounded-lg transition-colors',
          opts?.indent ? 'pl-1 pr-1' : 'px-1',
          isActive && !projectHubOpen
            ? 'bg-[#1f1f1f]'
            : 'hover:bg-[#161616]',
        )}
      >
        {renamingId === `chat:${chat.id}` ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commitRename();
              if (e.key === 'Escape') setRenamingId(null);
            }}
            className="flex-1 min-w-0 bg-[#111] border border-[#2a2a2a] rounded-md px-1.5 py-1.5 text-[13px] text-[#e8e6e3] focus:outline-none focus:border-[#3a3a3a]"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              onLeaveProjectHub?.();
              void selectChat(chat.id);
            }}
            className={cn(
              'flex-1 min-w-0 text-left text-[13px] py-2 px-2 truncate leading-snug',
              isActive && !projectHubOpen
                ? 'text-[#e8e6e3]'
                : 'text-[#8a8884] group-hover:text-[#c8c6c3]',
            )}
          >
            {chat.title || 'New chat'}
          </button>
        )}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                'p-1.5 mr-0.5 rounded-md text-[#3a3a3a] hover:text-[#e8e6e3] hover:bg-[#222] transition-opacity shrink-0',
                'opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100',
                isActive && !projectHubOpen && 'opacity-60',
              )}
              aria-label="Chat options"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="w-48 bg-[#1a1a1a] border-[#2a2a2a] text-[#e8e6e3] od-enter"
          >
            <DropdownMenuItem
              onClick={() => startRename(chat.id, chat.title, 'chat')}
              className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]"
            >
              <Pencil className="w-3.5 h-3.5 mr-2 opacity-60" />
              Rename
            </DropdownMenuItem>
            {projects.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]">
                  <FolderInput className="w-3.5 h-3.5 mr-2 opacity-60" />
                  Move to project
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e8e6e3] max-w-[200px]">
                  {projects.map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      disabled={chat.projectId === p.id}
                      onClick={() => void moveChatToProject(chat.id, p.id)}
                      className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]"
                    >
                      <Folder className="w-3.5 h-3.5 mr-2 opacity-60 shrink-0" />
                      <span className="truncate">{p.name}</span>
                    </DropdownMenuItem>
                  ))}
                  {chat.projectId && (
                    <>
                      <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                      <DropdownMenuItem
                        onClick={() => void moveChatToProject(chat.id, null)}
                        className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]"
                      >
                        <FolderOutput className="w-3.5 h-3.5 mr-2 opacity-60" />
                        Remove from project
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator className="bg-[#2a2a2a]" />
            <DropdownMenuItem
              onClick={() => {
                if (confirm('Delete this chat?')) void deleteChat(chat.id);
              }}
              className="cursor-pointer text-[13px] text-red-400/80 focus:bg-red-950/40 focus:text-red-300"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const accountMenu = currentUser ? (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'w-full flex items-center gap-2.5 rounded-xl text-left transition-colors',
            collapsed ? 'p-0.5 justify-center hover:bg-[#1a1a1a]' : 'px-2.5 py-2 hover:bg-[#1a1a1a]',
          )}
          title={collapsed ? displayName(currentUser) : undefined}
        >
          <Avatar shape={getAvatarShape(currentUser.metadata)} className="w-7 h-7 shrink-0">
            <AvatarImage src={currentUser.metadata.picture} alt="" />
            <AvatarFallback className="text-[10px] bg-[#1a1a1a] text-[#5a5a5a]">
              {displayName(currentUser).charAt(0)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-[#e8e6e3] truncate font-medium leading-tight">
                  {displayName(currentUser)}
                </p>
                <p className="text-[10px] text-[#3a3a3a] truncate mt-0.5">Nostr</p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-[#3a3a3a] shrink-0" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? 'right' : 'top'}
        align={collapsed ? 'end' : 'start'}
        className="w-56 bg-[#1a1a1a] border-[#2a2a2a] text-[#e8e6e3]"
      >
        {otherUsers.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[11px] text-[#5a5a5a] font-normal">
              Switch account
            </DropdownMenuLabel>
            {otherUsers.map((user) => (
              <DropdownMenuItem
                key={user.id}
                onClick={() => setLogin(user.id)}
                className="cursor-pointer focus:bg-[#222] focus:text-[#e8e6e3]"
              >
                <span className="truncate text-sm">{displayName(user)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-[#2a2a2a]" />
          </>
        )}
        <DropdownMenuItem
          onClick={() => onOpenSettings()}
          className="cursor-pointer focus:bg-[#222] focus:text-[#e8e6e3]"
        >
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setTimeout(() => removeLogin(currentUser.id), 0);
          }}
          className="cursor-pointer text-red-400 focus:bg-[#222] focus:text-red-400"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <button
      type="button"
      onClick={() => onOpenSettings()}
      className={cn(
        'rounded-xl text-[#8a8884] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors',
        collapsed
          ? 'p-2'
          : 'w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px]',
      )}
      title="Settings"
    >
      <Settings className="w-4 h-4 shrink-0" />
      {!collapsed && <span>Settings</span>}
    </button>
  );

  if (collapsed) {
    return (
      <div className="w-[52px] shrink-0 border-r border-[#1a1a1a] bg-[#0c0c0c] flex flex-col items-center py-3 gap-0.5">
        <button
          type="button"
          onClick={onToggle}
          className="p-2.5 rounded-xl text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
          title="Expand sidebar"
        >
          <PanelLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            onLeaveProjectHub?.();
            void createChat(null);
          }}
          className="p-2.5 rounded-xl text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
          title="New chat (⌘N)"
        >
          <SquarePen className="w-4 h-4" />
        </button>
        {onOpenSearch && (
          <button
            type="button"
            onClick={onOpenSearch}
            className="p-2.5 rounded-xl text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
            title="Search (⌘K)"
          >
            <Search className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            void createProject().then((p) => {
              if (onOpenProjectHub) onOpenProjectHub(p.id);
            });
          }}
          className="p-2.5 rounded-xl text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
          title="New project"
        >
          <FolderPlus className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <WorkspaceStatusChips onOpenSettings={onOpenSettings} compact />
        <div className="mt-1.5">{accountMenu}</div>
      </div>
    );
  }

  return (
    <aside className="w-[272px] shrink-0 border-r border-[#1a1a1a] bg-[#0c0c0c] flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 px-3 pt-3.5 pb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 px-1">
          <span
            className="text-[14px] font-semibold tracking-tight text-[#e8e6e3] select-none"
            style={{ letterSpacing: '-0.02em' }}
          >
            OpenDork
          </span>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors shrink-0"
          title="Collapse sidebar (⌘B)"
        >
          <PanelLeftClose className="w-4 h-4" />
        </button>
      </div>

      {/* Primary actions */}
      <div className="shrink-0 px-3 pb-2 space-y-1.5">
        <button
          type="button"
          onClick={() => {
            onLeaveProjectHub?.();
            void createChat(null);
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] text-[#e8e6e3] bg-[#171717] hover:bg-[#1e1e1e] border border-[#262626] transition-colors shadow-sm"
          title="New chat (⌘N)"
        >
          <SquarePen className="w-4 h-4 text-[#8a8884]" />
          <span className="font-medium">New chat</span>
          <kbd className="ml-auto hidden sm:inline text-[10px] text-[#3a3a3a] font-mono tracking-wide">
            ⌘N
          </kbd>
        </button>
        {onOpenSearch && (
          <button
            type="button"
            onClick={onOpenSearch}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] text-[#6a6864] hover:text-[#c8c6c3] hover:bg-[#141414] transition-colors"
            title="Search (⌘K)"
          >
            <Search className="w-4 h-4 opacity-70" />
            <span>Search</span>
            <kbd className="ml-auto hidden sm:inline text-[10px] text-[#3a3a3a] font-mono tracking-wide">
              ⌘K
            </kbd>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin">
        {/* Projects */}
        <div className="flex items-center justify-between px-2 pt-2 pb-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[#3a3a3a]">
            Projects
          </span>
          <button
            type="button"
            onClick={() => {
              void createProject().then((p) => {
                if (onOpenProjectHub) onOpenProjectHub(p.id);
              });
            }}
            className="p-1 rounded-md text-[#3a3a3a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
            title="New project"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        </div>

        {projects.length === 0 && (
          <p className="px-2.5 py-2 text-[11px] text-[#3a3a3a] leading-relaxed mb-1">
            Optional. Shared instructions, memory & context across chats.
          </p>
        )}

        {projects.map((project) => {
          const open = isExpanded(project.id);
          const isActiveProject =
            activeProject?.id === project.id &&
            (projectHubOpen || (!projectHubOpen && activeChat?.projectId === project.id));
          const isHubFocus = projectHubOpen && activeProject?.id === project.id;
          const projectChats = chats
            .filter((c) => c.projectId === project.id)
            .sort((a, b) => b.updatedAt - a.updatedAt);

          return (
            <div key={project.id} className="mb-0.5">
              <div
                className={cn(
                  'group flex items-center gap-0.5 px-1 rounded-lg transition-colors',
                  (isActiveProject || isHubFocus) && 'bg-[#171717]',
                  !isActiveProject && !isHubFocus && 'hover:bg-[#141414]',
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    setExpanded((e) => ({ ...e, [project.id]: !open }));
                  }}
                  className="p-1.5 text-[#3a3a3a] hover:text-[#8a8884] shrink-0"
                  aria-label={open ? 'Collapse project' : 'Expand project'}
                >
                  {open ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                </button>

                {renamingId === `project:${project.id}` ? (
                  <input
                    ref={renameInputRef}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => void commitRename()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void commitRename();
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    className="flex-1 min-w-0 bg-[#111] border border-[#2a2a2a] rounded-md px-1.5 py-1.5 text-[13px] text-[#e8e6e3] focus:outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => openProject(project.id)}
                    className={cn(
                      'flex-1 min-w-0 text-left text-[13px] py-2 truncate font-medium leading-snug',
                      isHubFocus || isActiveProject
                        ? 'text-[#e8e6e3]'
                        : 'text-[#8a8884] hover:text-[#e8e6e3]',
                    )}
                  >
                    {project.name}
                  </button>
                )}

                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="p-1.5 mr-0.5 rounded-md text-[#3a3a3a] opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 hover:text-[#e8e6e3] hover:bg-[#222] transition-opacity shrink-0"
                      aria-label="Project options"
                    >
                      <MoreHorizontal className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={4}
                    className="w-44 bg-[#1a1a1a] border-[#2a2a2a] text-[#e8e6e3] od-enter"
                  >
                    <DropdownMenuItem
                      onClick={() => openProjectEdit(project.id)}
                      className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-2 opacity-60" />
                      Open project
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => startRename(project.id, project.name, 'project')}
                      className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]"
                    >
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => void exportProject(project.id)}
                      className="cursor-pointer text-[13px] focus:bg-[#222] focus:text-[#e8e6e3]"
                    >
                      <Download className="w-3.5 h-3.5 mr-2 opacity-60" />
                      Export
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                    <DropdownMenuItem
                      onClick={() => {
                        if (confirm(`Delete project “${project.name}” and all its chats?`)) {
                          void deleteProject(project.id);
                        }
                      }}
                      className="cursor-pointer text-[13px] text-red-400/80 focus:bg-red-950/40 focus:text-red-300"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {open && (
                <div className="ml-3.5 mt-0.5 space-y-0.5 border-l border-[#1a1a1a] pl-1.5">
                  {projectChats.map((chat) => renderChatRow(chat, { indent: true }))}
                  <button
                    type="button"
                    onClick={() => {
                      onLeaveProjectHub?.();
                      void createChat(project.id);
                    }}
                    className="mx-0.5 w-[calc(100%-4px)] px-2 py-1.5 text-[11px] text-[#3a3a3a] hover:text-[#8a8884] transition-colors flex items-center gap-1.5 rounded-md hover:bg-[#141414]"
                  >
                    <MessageSquarePlus className="w-3 h-3" />
                    New chat
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Recents */}
        <div className="mt-4 mb-1">
          <div className="flex items-center justify-between px-2 pt-1 pb-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wider text-[#3a3a3a]">
              Recents
            </span>
          </div>
          <div className="space-y-0.5">
            {visibleRecents.length === 0 && (
              <p className="px-2.5 py-2 text-[11px] text-[#3a3a3a] leading-relaxed">
                New chats land here. Add a project when you want shared memory.
              </p>
            )}
            {visibleRecents.map((chat) => renderChatRow(chat))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-[#1a1a1a] p-2 space-y-1">
        <WorkspaceStatusChips onOpenSettings={onOpenSettings} />
        {accountMenu}
      </div>
    </aside>
  );
}
